import type { RowAction } from '@/components/ui/data-table/types';
import { ticketMutations } from '@/features/tickets/tickets.queries';
import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import AuthService from '@/services/AuthService';
import { executeMutation } from '@/utils/executeMutation';
import {
  createTicketEvent,
  type Ticket,
  type TicketEvent,
} from '@certhon/models';
import { Bell, BellOff, CheckCircle, FileDown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export { adaptActionsForPanel } from '@/utils/adaptActionsForPanel';

/**
 * Get actions for a ticket - works for both row actions (table) and panel actions
 *
 * Status and notification actions are split into two distinct set-state actions
 * each (Close/Reopen, Enable/Disable notifications) instead of a single toggle.
 * Each action declares a `disabled` predicate restricting it to rows where it
 * would yield a change, and is wired with a single-roundtrip bulk handler.
 */
export function getTicketActions(userId: number | null): RowAction<Ticket>[] {
  const actions: RowAction<Ticket>[] = [];

  // Close — applicable to opened tickets only.
  actions.push({
    id: 'close',
    icon: CheckCircle,
    label: 'Close',
    disabled: (ticket) => ticket.status !== 'opened',
    hideWhenDisabled: true,
    onCalled: async (ticket) => {
      if (!userId) return;
      await executeMutation(ticketMutations.postEvents(), [
        buildSetStatusEvent(ticket, userId, 'closed'),
      ]);
    },
    bulk: userId
      ? {
          tier: 'bulk',
          onCalledBulk: async (tickets) => {
            const bulkOperationId = uuidv4();
            const events: TicketEvent[] = tickets.map((t) =>
              buildSetStatusEvent(t, userId, 'closed', { bulkOperationId }),
            );
            await executeMutation(ticketMutations.postEvents(), events);
          },
        }
      : undefined,
  });

  // Reopen — applicable to closed tickets only.
  actions.push({
    id: 'reopen',
    icon: RotateCcw,
    label: 'Reopen',
    disabled: (ticket) => ticket.status !== 'closed',
    hideWhenDisabled: true,
    onCalled: async (ticket) => {
      if (!userId) return;
      await executeMutation(ticketMutations.postEvents(), [
        buildSetStatusEvent(ticket, userId, 'opened'),
      ]);
    },
    bulk: userId
      ? {
          tier: 'bulk',
          onCalledBulk: async (tickets) => {
            const bulkOperationId = uuidv4();
            const events: TicketEvent[] = tickets.map((t) =>
              buildSetStatusEvent(t, userId, 'opened', { bulkOperationId }),
            );
            await executeMutation(ticketMutations.postEvents(), events);
          },
        }
      : undefined,
  });

  // Enable notifications — applicable when current user is NOT opted-in.
  if (userId) {
    actions.push({
      id: 'enable-notifications',
      icon: Bell,
      label: 'Enable notifications',
      disabled: (ticket) =>
        ticket.notification_opt_in?.includes(userId) ?? false,
      hideWhenDisabled: true,
      onCalled: async (ticket) => {
        await executeMutation(ticketMutations.postEvents(), [
          buildNotificationEvent(ticket, userId, 'NOTIFICATION_OPT_IN'),
        ]);
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (tickets) => {
          const bulkOperationId = uuidv4();
          const events: TicketEvent[] = tickets.map((t) =>
            buildNotificationEvent(t, userId, 'NOTIFICATION_OPT_IN', {
              bulkOperationId,
            }),
          );
          await executeMutation(ticketMutations.postEvents(), events);
        },
      },
    });

    // Disable notifications — applicable when current user IS opted-in.
    actions.push({
      id: 'disable-notifications',
      icon: BellOff,
      label: 'Disable notifications',
      disabled: (ticket) =>
        !(ticket.notification_opt_in?.includes(userId) ?? false),
      hideWhenDisabled: true,
      onCalled: async (ticket) => {
        await executeMutation(ticketMutations.postEvents(), [
          buildNotificationEvent(ticket, userId, 'NOTIFICATION_OPT_OUT'),
        ]);
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (tickets) => {
          const bulkOperationId = uuidv4();
          const events: TicketEvent[] = tickets.map((t) =>
            buildNotificationEvent(t, userId, 'NOTIFICATION_OPT_OUT', {
              bulkOperationId,
            }),
          );
          await executeMutation(ticketMutations.postEvents(), events);
        },
      },
    });
  }

  // Download PDF action — per-row direct link, bulk via POST /tickets/pdf.
  actions.push({
    id: 'download-pdf',
    icon: FileDown,
    label: 'Download PDF',
    onCalled: (ticket) => {
      const link = document.createElement('a');
      link.href = `/api/tickets/${ticket.recnum}/pdf`;
      link.download = `ticket-${ticket.recnum}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    bulk: {
      tier: 'bulk',
      onCalledBulk: async (tickets) => {
        const recnums = tickets
          .map((t) => t.recnum)
          .filter((r): r is number => typeof r === 'number');
        if (recnums.length === 0) return;

        const toastId = toast.loading(
          `Generating PDF for ${recnums.length} ticket${recnums.length > 1 ? 's' : ''}...`,
        );
        try {
          await AuthService.getInstance().waitForReady();
          const response = await fetch(resolveUrl('/tickets/pdf'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ recnums }),
          });
          await verifyResponse(response);

          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `tickets-report_${new Date().toISOString().split('T')[0]}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 60000);

          toast.success('PDF downloaded successfully', { id: toastId });
        } catch (error) {
          toast.error(
            `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
            { id: toastId },
          );
          throw error;
        }
      },
    },
  });

  return actions;
}

function buildSetStatusEvent(
  ticket: Ticket,
  userId: number,
  status: 'opened' | 'closed',
  meta?: { bulkOperationId: string },
): TicketEvent {
  return createTicketEvent('UPDATE_TICKET', {
    ticket_id: ticket.id,
    user_id: userId,
    payload: { status },
    ...(meta ? { meta } : {}),
  });
}

function buildNotificationEvent(
  ticket: Ticket,
  userId: number,
  type: 'NOTIFICATION_OPT_IN' | 'NOTIFICATION_OPT_OUT',
  meta?: { bulkOperationId: string },
): TicketEvent {
  return createTicketEvent(type, {
    ticket_id: ticket.id,
    user_id: userId,
    payload: {},
    ...(meta ? { meta } : {}),
  });
}
